import clsx from 'clsx';
import {StationsContext} from 'context/StationsContext';
import {useTranslation} from 'hooks/translations/useCustomTranslation';
import {useRouter} from 'next/router';
import React, {FC, useContext, useMemo, useState} from 'react';

import {SxProps} from '@mui/material';

import {Station} from 'lib/api/backend.schemas';
import {TypoComponentsCityStationInfo} from 'lib/api/strapi';

import {formatTime} from 'utils/date/formatDate';

import {palette} from 'styles/mui/scss';

import {Box, Grid, Typography} from 'components/basic-components';
import Breadcrumb from 'components/basic-components/Breadcrumb/Breadcrumb';
import {CustomIcon} from 'components/basic-components/CustomIcon/CustomIcon';
import MarkdownContent from 'components/basic-components/MarkdownContent';
import {NextLink} from 'components/basic-components/NextLink/Link';
import StationLabels from 'components/basic-components/StationLabels';
import {StarcarMap} from 'components/basic-components/StationMap/StarcarMap';
import {withErrorBoundary} from 'components/error/ErrorBoundary';
import ContentWrapper from 'components/layout-components/ContentWrapper/ContentWrapper';
import BPRModal from 'components/section-components/bpr/BPRModal/BPRModal';

import styles from './StationInformationCity.module.scss';
import StationImage from 'components/basic-components/StationImage';

export const BusinessHours = ({station, sx}: {station: Station; sx?: SxProps}) => {
  const {t} = useTranslation('common');
  if (!station) return null;

  return (
    <Box sx={{display: 'flex', ...sx}}>
      <Box className={styles.businessHoursDaysWrapper}>
        <Typography>{t('moFr')}:</Typography>
        <Typography>{t('sa')}:</Typography>
        <Typography>{t('su')}:</Typography>
      </Box>
      <Box>
        {station?.businessHours.all[1].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[1].from)} -{' '}
            {formatTime(station?.businessHours.all[1].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
        {station?.businessHours.all[6].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[6].from)} -{' '}
            {formatTime(station?.businessHours.all[6].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
        {station?.businessHours.all[0].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[0].from)} -{' '}
            {formatTime(station?.businessHours.all[0].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
      </Box>
    </Box>
  );
};

export const Address = ({station, sx}: {station: Station; sx?: SxProps}) => {
  const {t} = useTranslation('common');
  if (!station) return null;

  return (
    <Box sx={{...sx}}>
      {station?.remoteStation ? (
        <Box>
          <Typography fontWeight={700}>{t('meetingPoint')}</Typography>
          <Typography>{station?.subName}</Typography>
        </Box>
      ) : (
        <Box>
          <Typography>{station?.street}</Typography>
          <Typography>
            {station?.zip} {station?.city}
          </Typography>
        </Box>
      )}
      <Box sx={{margin: '10px 0'}}>
        {station?.remoteStation && station?.relatedStation ? (
          <Box>
            <Typography fontWeight={700}>{t('relatedStation')}</Typography>
            <Typography>{station?.relatedStation?.description}</Typography>
          </Box>
        ) : (
          <Box>
            <Typography>
              {station?.phone ? (
                <a style={{color: 'inherit'}} href={`tel:${station.phone}`}>
                  {t('telefon')}: {station?.phone}
                </a>
              ) : (
                '\u00A0'
              )}
            </Typography>
            <Typography>{station?.fax ? `${t('fax')}: ${station?.fax}` : '\u00A0'}</Typography>
          </Box>
        )}
        <Typography>
          {station?.email ? (
            <a style={{color: 'inherit'}} href={`mailto:${station.email}`}>
              {station.email}
            </a>
          ) : (
            '\u00A0'
          )}
        </Typography>
      </Box>
    </Box>
  );
};

const StationInformationCity: FC<TypoComponentsCityStationInfo> = ({
  title,
  teaserText,
  text,
  headerLevel,
  headTitle,
  headTitleLevel,
  breadcrumb,
}) => {
  const {t} = useTranslation('common');
  const stations = useContext(StationsContext);
  const stationRefs = useMemo(() => {
    const refs = {};
    stations.forEach(station => {
      refs[station.id] = React.createRef();
    });
    return refs;
  }, [stations]);

  const [expandText, setExpandText] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedStation, setSelectedStation] = useState<Station>();

  const {asPath} = useRouter();

  const handleMarkerClick = id => {
    const ref = stationRefs[id];
    if (ref && ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop - 100,
        behavior: 'smooth',
      });
    }
  };

  const handleCTA = station => {
    setModalOpen(true);
    setSelectedStation(station);
  };

  return (
    <ContentWrapper>
      <BPRModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        presetStation={selectedStation?.id}
      />
      <Box className={styles.wrapper}>
        <Breadcrumb link={breadcrumb || [{linkText: stations[0]?.city}]} />
        <Typography component={headTitleLevel || 'h2'} className={styles.title}>
          {headTitle || stations[0]?.city}
        </Typography>
        <StarcarMap
          key={asPath}
          onClickMarker={handleMarkerClick}
          activeStation={undefined}
          stations={stations}
        />
        {title ? (
          <Typography
            variant="h2"
            component={headerLevel || 'h2'}
            className={styles.stationInfoTitle}
          >
            {title}
          </Typography>
        ) : null}
        <Box sx={!title ? {marginTop: 10} : {}} className={styles.textWrapper}>
          <MarkdownContent className={styles.text} content={teaserText} />
          <MarkdownContent
            className={clsx({
              [styles.expandedText]: true,
              [styles.showExpandedText]: expandText,
            })}
            content={text}
          />
          {text ? (
            <Typography
              onClick={() => setExpandText(!expandText)}
              className={styles.showMoreButton}
            >
              {expandText ? t('showLess') : t('showMore')}
            </Typography>
          ) : null}
        </Box>
        <Grid container columnSpacing={'2%'}>
          {stations.map(station => {
            return (
              <Grid ref={stationRefs[station.id]} key={station.id} xxs={12} md={6} xl={4}>
                <Box sx={{position: 'relative', marginBottom: '14px'}}>
                  <NextLink href={station?.url}>
                    <Box className={styles.overlay} />
                    <Box className={styles.labelsWrapper}>
                      <StationLabels sx={{justifyContent: 'flex-end'}} station={station} />
                    </Box>
                    <Typography variant="teaser" className={styles.stationName}>
                      {station?.name}
                    </Typography>
                      <StationImage station={station} className={styles.image} />
                    {/* STARCAR-1844 uncomment when stations have correct pictures
                     {station?.picture ? (
                      <Image
                        src={station.picture}
                        height={500}
                        width={500}
                        alt=""
                        className={styles.image}
                      />
                    ) : (
                      <StationFallbackImage className={styles.image} />
                    )} */}
                  </NextLink>
                </Box>
                <Box sx={{color: palette.colorGrey80, '& p': {lineHeight: '24px'}}}>
                  <Address station={station} />
                  {station?.remoteStation && station?.relatedStation ? (
                    <BusinessHours station={station} />
                  ) : (
                    <BusinessHours station={station} />
                  )}
                  <Box className={styles.ctaElement} onClick={() => handleCTA(station)}>
                    <Typography sx={{fontWeight: '700'}}>{t('stationBookingButton')}</Typography>
                    <CustomIcon name="arrow" className={styles.icon} />
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </ContentWrapper>
  );
};

export default withErrorBoundary(StationInformationCity, ' StationInformationCity');
